import camelcaseKeys from 'camelcase-keys'
import throttle from 'lodash.throttle'
import { createStore } from 'zustand'

import { executeGet, executePost, executePut } from '@/services/lukita'
import { DOCUMENT_STATUSES } from '../constants'
import type { DocumentStore } from './types'

export const createDocumentStore = () =>
  createStore<DocumentStore>((set, get) => ({
    loadingDocument: true,
    mutating: false,
    document: null,
    pages: [],
    currentPage: 0,
    currentBlock: null,

    actions: {
      startLoadingDocument: () => set({ loadingDocument: true }),
      stopLoadingDocument: () => set({ loadingDocument: false }),
      setDocument: (document) => set({ document: camelcaseKeys(document) }),
      setDocumentStatus: (status) =>
        set((state) => {
          if (!state.document) return {}
          return {
            document: { ...state.document, status },
          }
        }),
      setDocumentPages: (pages) => set({ pages: camelcaseKeys(pages) }),
      setCurrentPage: (index) =>
        set({ currentPage: index, currentBlock: null }),
      setCurrentPageFromNumber: (number) => {
        const index = get().pages.findIndex((page) => page.number === number)
        get().actions.setCurrentPage(index)
      },
      setCurrentBlock: (index) => set({ currentBlock: index }),
    },

    mutations: {
      ocrDocument: async () => {
        const { document } = get()
        if (!document) return

        const { setDocumentStatus } = get().actions
        setDocumentStatus(DOCUMENT_STATUSES.enqueued)
        const path = `/documents/ocr`
        const body = { document_ids: [document.id] }

        try {
          await executePost(path, { ...body, folder_id: document?.folderId })
        } catch (error) {
          setDocumentStatus(DOCUMENT_STATUSES.notStarted)
          throw error
        }
      },
      updateDocument: async (body) => {
        set({ mutating: true })
        const document = get().document
        if (!document) return

        const path = `/documents/${document.id}`
        const response = await executePut(path, body).finally(() =>
          set({ mutating: false }),
        )

        const updateDocument = await response.json()
        get().actions.setDocument(updateDocument)
        return updateDocument
      },
    },

    loaders: {
      fetchDocument: throttle(async (id) => {
        get().actions.startLoadingDocument()

        const response = await executeGet(`/documents/${id}`)
        const document = await response.json()
        const pages = document.pages
        delete document.pages

        get().actions.setDocument(document)
        get().actions.setDocumentPages(pages)
        get().actions.stopLoadingDocument()
      }, 500),
    },
  }))
